import { Controller } from "@hotwired/stimulus"
import { useDocumentListener, useWindowListener } from 'lib/event_helpers';
import { postCancelFullscreen, postRequestFullscreen } from "lib/post_messages";
import { isMobileWidth } from "lib/window_helper";

const FULLSCREEN_CLASS = 'dewey--fullscreen';

export default class extends Controller {
  static targets = ['chatWindow', 'suggestions', 'input', 'keywordCitations'];
  static values = { enabled: Boolean, mobileOnly: Boolean };

  lastTouch = null;

  connect() {
    // Intercept touch inputs to avoid iPhone strangeness
    useDocumentListener(this, 'touchstart', this.recordTouch);
    useDocumentListener(this, 'touchmove', this.interruptEvent, { passive: false });
    useDocumentListener(this, 'touchend', this.endTouch);

    // Listen for resizing to cancel fullscreen
    useWindowListener(this, 'resize', this.checkWindowSize);

    // The whole iframe reloaded, let's bail on fullscreen mode
    this.cancel();
  }


  // Toggle fullscreen mode

  checkWindowSize() {
    // Automatically cancel fullscreen mode if the window grows
    // above our valid size
    if (this.mobileOnlyValue && !isMobileWidth()) {
      this.cancel();
    }
  }

  isFullscreen() {
    return document.body.classList.contains(FULLSCREEN_CLASS);
  }

  toggle() {
    // On a desktop modal, clicking outside `main` closes the modal
    if (this.isFullscreen())
      this.cancel();
    else
      this.start();
  }

  captureEvent(event) {
    // Prevents clicks on `main` to propogate to `body` (which would trigger `toggle`)
    if (this.isFullscreen()) {
      event.stopPropagation();
    }
  }

  start() {
    const allowFullscreen =
      this.enabledValue && // Context has fullscreen enabled
      (isMobileWidth() || !this.mobileOnlyValue) && // The screen is a valid size
      !this.isFullscreen(); // We're not already fullscreen

    if (allowFullscreen) {
      document.body.classList.add(FULLSCREEN_CLASS);
      postRequestFullscreen();

      // If the textarea is focused when we go fullscreen, we need to momentarily
      // lose focus so that the mobile device can show its keyboard properly
      if (this.hasInputTarget && this.inputTarget === document.activeElement) {
        this.inputTarget.blur();
        setTimeout(() => {
          this.inputTarget.focus();
        }, 0);
      }
    }
  }

  cancel() {
    if (this.isFullscreen()) {
      postCancelFullscreen();
      document.body.classList.remove(FULLSCREEN_CLASS);

      this.dispatch('cancel');
    }
  }


  // Prevent weird scroll behavior on iOS when the
  // keyboard appears

  recordTouch(event) {
    this.lastTouch = event.touches[0];
  }

  interruptEvent(event) {
    const touch = event.touches[0];

    if (this.isFullscreen()) {
      const { chatWindowTarget, hasSuggestionsTarget, hasKeywordCitationsTarget } = this;

      let shouldInterrupt = true;
      let target = event.target;
      do {
        if (
          hasSuggestionsTarget && target === this.suggestionsTarget ||
          hasKeywordCitationsTarget && target === this.keywordCitationsTarget
        ) {
          // Allow horizontal scrolling of suggestions
          shouldInterrupt = !this.isScrollingHorizontally(touch);
          break;
        } else if (target === chatWindowTarget) {
          // Allow scrolling a scrollable chat window
          shouldInterrupt = !this.isChatWindowScrollable();
        }

        target = target.parentElement;
      } while (target);

      // We want to interrupt touch events that will be forwarded to the main window
      // since on iOS it's impossible to make the window not scroll while the keyboard
      // is up (unless we're inside an app)
      if (shouldInterrupt) {
        event.preventDefault();
        event.stopPropagation();
        this.lastTouch = null;
        return false;
      }
    }

    this.lastTouch = touch;
  }

  isScrollingHorizontally(touch) {
    // Scrolling horizontally on the suggestionsTarget
    const { lastTouch } = this;
    if (lastTouch != null) {
      const verticalDistance = Math.abs(touch.clientY - lastTouch.clientY);
      const horizontalDistance = Math.abs(touch.clientX - lastTouch.clientX);
      return horizontalDistance > verticalDistance;
    }
    return false;
  }

  isChatWindowScrollable() {
    // Chat window is bigger than the height provided to it, so it can scroll
    const { chatWindowTarget } = this;
    const { scrollHeight, offsetHeight } = chatWindowTarget;
    return scrollHeight > offsetHeight;
  }

  endTouch(event) {
    this.lastTouch = null;
  }
}